import { GeneratedGenieDocument, GenieDocumentBrand } from "@/types/genie-documents/genie-document";

/**
 * Native jsPDF renderer for co-branded genie documents.
 *
 * We deliberately do NOT rasterize the HTML preview: that slices text lines in
 * half at page boundaries and bloats the file with a flat image. Instead we lay
 * the document out as real vector text — word-wrapped, page-broken BETWEEN lines,
 * with selectable copy and sharp logos. The design mirrors DocumentPreview:
 * co-brand masthead (customer leads, Help Genie present), brand gradient rule,
 * eyebrow, numbered section chips, and a "Powered by Help Genie" footer.
 */

const TEMPLATE_EYEBROW: Record<string, string> = {
  faq: "Frequently Asked Questions",
  "how-to": "Step-by-Step Guide",
  "one-pager": "Overview",
};

const sanitize = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || "document";

type RGB = { r: number; g: number; b: number };

const hexToRgb = (hex: string): RGB => {
  let h = (hex || "").trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  if (h.length !== 6 || Number.isNaN(n)) return { r: 78, g: 156, b: 255 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);
const lerpRgb = (a: RGB, b: RGB, t: number): RGB => ({
  r: lerp(a.r, b.r, t),
  g: lerp(a.g, b.g, t),
  b: lerp(a.b, b.b, t),
});

interface LoadedImage {
  dataUrl: string;
  w: number;
  h: number;
}

/** Load an image and convert to a PNG data URL for jsPDF. Returns null on failure (CORS/404). */
const loadImage = (src: string | null | undefined, crossOrigin: boolean): Promise<LoadedImage | null> =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    if (crossOrigin) img.crossOrigin = "anonymous";
    const done = (v: LoadedImage | null) => resolve(v);
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return done(null);
        ctx.drawImage(img, 0, 0);
        done({ dataUrl: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch {
        done(null); // tainted canvas — skip the logo rather than break the export
      }
    };
    img.onerror = () => done(null);
    img.src = src;
    setTimeout(() => done(null), 3000);
  });

type Run = { text: string; bold: boolean };

/** Split **bold** markers into styled runs. */
const parseRuns = (text: string): Run[] => {
  const runs: Run[] = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) runs.push({ text: text.slice(last, m.index), bold: false });
    runs.push({ text: m[1], bold: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last), bold: false });
  return runs.length ? runs : [{ text, bold: false }];
};

type Block =
  | { type: "p"; text: string }
  | { type: "ul" | "ol"; items: string[] };

/** Group body text into paragraphs and bullet/numbered lists (mirrors DocumentPreview). */
const parseBlocks = (body: string): Block[] => {
  const blocks: Block[] = [];
  let list: { type: "ul" | "ol"; items: string[] } | null = null;
  const flush = () => {
    if (list) blocks.push(list);
    list = null;
  };
  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const bullet = line.match(/^[-*]\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    if (bullet) {
      if (list && list.type !== "ul") flush();
      if (!list) list = { type: "ul", items: [] };
      list.items.push(bullet[1]);
    } else if (numbered) {
      if (list && list.type !== "ol") flush();
      if (!list) list = { type: "ol", items: [] };
      list.items.push(numbered[1]);
    } else {
      flush();
      blocks.push({ type: "p", text: line });
    }
  }
  flush();
  return blocks;
};

interface DocumentImages {
  customerLogo: LoadedImage | null;
  hgWordmark: LoadedImage | null;
}

/**
 * Pure layout pass — builds and returns the jsPDF instance. Kept free of DOM
 * download/image-loading so it can be exercised outside the browser (tests).
 */
export async function buildGenieDocumentPdf(
  doc: GeneratedGenieDocument,
  brand: GenieDocumentBrand,
  images: DocumentImages,
) {
  const { default: jsPDF } = await import("jspdf");
  const { customerLogo, hgWordmark } = images;

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginX = 54;
  const contentW = pageW - marginX * 2;
  const footerLineY = pageH - 46;
  const contentBottom = footerLineY - 16;
  const contentTop = 54;

  const primary = hexToRgb(brand.primaryColor || "#4E9CFF");
  const secondary = hexToRgb(brand.secondaryColor || "#FF4DB8");
  const FONT = "helvetica";

  const st = { y: 0 };

  const setColor = (c: number[]) => pdf.setTextColor(c[0], c[1], c[2]);

  const truncate = (text: string, maxW: number, fs: number, weight: "normal" | "bold") => {
    pdf.setFont(FONT, weight);
    pdf.setFontSize(fs);
    if (pdf.getTextWidth(text) <= maxW) return text;
    let s = text;
    while (s.length > 1 && pdf.getTextWidth(s + "…") > maxW) s = s.slice(0, -1);
    return s + "…";
  };

  const fitContain = (img: LoadedImage, boxW: number, boxH: number) => {
    const ratio = img.w / img.h;
    return boxW / boxH > ratio ? { w: boxH * ratio, h: boxH } : { w: boxW, h: boxW / ratio };
  };

  const drawGradientBar = (x: number, y: number, w: number, h: number) => {
    const steps = 100;
    const sw = w / steps;
    for (let i = 0; i < steps; i++) {
      const c = lerpRgb(primary, secondary, i / (steps - 1));
      pdf.setFillColor(c.r, c.g, c.b);
      pdf.rect(x + i * sw, y, sw + 0.7, h, "F");
    }
  };

  type Tok = { t: string; bold: boolean; sp: boolean };

  const wrap = (runs: Run[], maxWidth: number, fs: number, forceBold: boolean): Tok[][] => {
    const tokens: Tok[] = [];
    for (const r of runs) {
      for (const p of r.text.split(/(\s+)/)) {
        if (p !== "") tokens.push({ t: p, bold: r.bold, sp: /^\s+$/.test(p) });
      }
    }
    const wof = (tok: Tok) => {
      pdf.setFont(FONT, tok.bold || forceBold ? "bold" : "normal");
      pdf.setFontSize(fs);
      return pdf.getTextWidth(tok.t);
    };
    const lines: Tok[][] = [];
    let line: Tok[] = [];
    let w = 0;
    for (const tok of tokens) {
      const tw = wof(tok);
      if (tok.sp) {
        if (line.length) {
          line.push(tok);
          w += tw;
        }
        continue;
      }
      if (w + tw > maxWidth && line.length) {
        while (line.length && line[line.length - 1].sp) {
          w -= wof(line[line.length - 1]);
          line.pop();
        }
        lines.push(line);
        line = [];
        w = 0;
      }
      line.push(tok);
      w += tw;
    }
    while (line.length && line[line.length - 1].sp) line.pop();
    if (line.length) lines.push(line);
    return lines;
  };

  const drawFooter = () => {
    pdf.setDrawColor(229, 234, 240);
    pdf.setLineWidth(0.6);
    pdf.line(marginX, footerLineY, pageW - marginX, footerLineY);
    const fy = footerLineY + 8;
    setColor([156, 163, 175]);
    pdf.setFont(FONT, "normal");
    pdf.setFontSize(8);
    pdf.text(truncate(brand.name, contentW * 0.5, 8, "normal"), marginX, fy, { baseline: "top" });
    if (hgWordmark) {
      const h = 11;
      const w = h * (hgWordmark.w / hgWordmark.h);
      const wx = pageW - marginX - w;
      pdf.addImage(hgWordmark.dataUrl, "PNG", wx, fy - 2, w, h);
      const label = "Powered by";
      pdf.setFontSize(8);
      setColor([156, 163, 175]);
      pdf.text(label, wx - 6, fy, { baseline: "top", align: "right" });
    } else {
      pdf.text("Powered by Help Genie", pageW - marginX, fy, { baseline: "top", align: "right" });
    }
  };

  const drawMasthead = () => {
    const top = 46;
    const logoSize = 38;
    if (customerLogo) {
      const { w, h } = fitContain(customerLogo, logoSize, logoSize);
      pdf.addImage(customerLogo.dataUrl, "PNG", marginX + (logoSize - w) / 2, top + (logoSize - h) / 2, w, h);
    } else {
      pdf.setFillColor(primary.r, primary.g, primary.b);
      pdf.roundedRect(marginX, top, logoSize, logoSize, 8, 8, "F");
      setColor([255, 255, 255]);
      pdf.setFont(FONT, "bold");
      pdf.setFontSize(20);
      pdf.text((brand.name || "G").trim().charAt(0).toUpperCase(), marginX + logoSize / 2, top + logoSize / 2, {
        align: "center",
        baseline: "middle",
      });
    }

    const tx = marginX + logoSize + 12;
    const tw = contentW - logoSize - 12 - 130;
    setColor([17, 24, 39]);
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(13);
    if (brand.tagline) {
      pdf.text(truncate(brand.name, tw, 13, "bold"), tx, top + 5, { baseline: "top" });
      pdf.setFont(FONT, "normal");
      pdf.setFontSize(8.5);
      setColor([107, 114, 128]);
      pdf.text(truncate(brand.tagline, tw, 8.5, "normal"), tx, top + 22, { baseline: "top" });
    } else {
      pdf.text(truncate(brand.name, tw, 13, "bold"), tx, top + 12, { baseline: "top" });
    }

    const rightY = top + 2;
    pdf.setFont(FONT, "normal");
    pdf.setFontSize(6.5);
    setColor([156, 163, 175]);
    pdf.text("MADE WITH", pageW - marginX, rightY, { align: "right", baseline: "top", charSpace: 0.8 });
    if (hgWordmark) {
      const h = 13;
      const w = h * (hgWordmark.w / hgWordmark.h);
      pdf.addImage(hgWordmark.dataUrl, "PNG", pageW - marginX - w, rightY + 11, w, h);
    } else {
      pdf.setFont(FONT, "bold");
      pdf.setFontSize(11);
      setColor([34, 43, 69]);
      pdf.text("Help Genie", pageW - marginX, rightY + 11, { align: "right", baseline: "top" });
    }

    const barY = top + logoSize + 14;
    drawGradientBar(marginX, barY, contentW, 5);

    st.y = barY + 5 + 22;
    const eyebrow = TEMPLATE_EYEBROW[doc.template] ?? "Guide";
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(8.5);
    setColor([primary.r, primary.g, primary.b]);
    pdf.text(eyebrow.toUpperCase(), marginX, st.y, { baseline: "top", charSpace: 1.3 });
    st.y += 16;

    flowText(parseRuns(doc.title), marginX, contentW, 21, 25, [17, 24, 39], true);
    if (doc.subtitle) {
      st.y += 4;
      flowText(parseRuns(doc.subtitle), marginX, contentW, 11, 15, [107, 114, 128], false);
    }
    st.y += 22;
  };

  const startPage = (first: boolean) => {
    if (!first) pdf.addPage();
    drawFooter();
    if (first) drawMasthead();
    else st.y = contentTop;
  };

  const ensure = (h: number) => {
    if (st.y + h > contentBottom) startPage(false);
  };

  function flowText(
    runs: Run[],
    x: number,
    maxWidth: number,
    fs: number,
    lh: number,
    color: number[],
    forceBold: boolean,
  ) {
    const lines = wrap(runs, maxWidth, fs, forceBold);
    for (const line of lines) {
      ensure(lh);
      let cx = x;
      setColor(color);
      for (const tok of line) {
        pdf.setFont(FONT, tok.bold || forceBold ? "bold" : "normal");
        pdf.setFontSize(fs);
        pdf.text(tok.t, cx, st.y, { baseline: "top" });
        cx += pdf.getTextWidth(tok.t);
      }
      st.y += lh;
    }
  }

  const drawList = (block: { type: "ul" | "ol"; items: string[] }) => {
    const bodyLeft = marginX + 30;
    const bodyWidth = contentW - 30;
    const isOl = block.type === "ol";
    const indent = isOl ? 18 : 14;
    block.items.forEach((item, idx) => {
      ensure(14.8);
      const markTop = st.y;
      pdf.setFont(FONT, isOl ? "bold" : "normal");
      pdf.setFontSize(10.5);
      setColor([primary.r, primary.g, primary.b]);
      pdf.text(isOl ? `${idx + 1}.` : "•", bodyLeft, markTop, { baseline: "top" });
      flowText(parseRuns(item), bodyLeft + indent, bodyWidth - indent, 10.5, 14.8, [55, 65, 81], false);
      st.y += 3;
    });
  };

  // ── build the document ───────────────────────────────────────────────────
  startPage(true);

  const bodyLeft = marginX + 30;
  const bodyWidth = contentW - 30;

  doc.sections.forEach((section, i) => {
    ensure(48); // keep the chip + heading together; break before an orphan
    const chipTop = st.y;
    pdf.setFillColor(primary.r, primary.g, primary.b);
    pdf.roundedRect(marginX, chipTop, 18, 18, 5, 5, "F");
    setColor([255, 255, 255]);
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(9.5);
    pdf.text(String(i + 1), marginX + 9, chipTop + 9.5, { align: "center", baseline: "middle" });

    flowText(parseRuns(section.heading), bodyLeft, bodyWidth, 12.5, 15.5, [31, 41, 55], true);
    st.y += 5;

    for (const block of parseBlocks(section.body)) {
      if (block.type === "p") {
        flowText(parseRuns(block.text), bodyLeft, bodyWidth, 10.5, 14.8, [55, 65, 81], false);
        st.y += 6;
      } else {
        drawList(block);
        st.y += 6;
      }
    }
    st.y += 16;
  });

  // Page numbers (only when it runs long).
  const total = pdf.internal.getNumberOfPages();
  if (total > 1) {
    for (let p = 1; p <= total; p++) {
      pdf.setPage(p);
      pdf.setFont(FONT, "normal");
      pdf.setFontSize(7.5);
      setColor([176, 182, 194]);
      pdf.text(`${p} / ${total}`, pageW / 2, footerLineY + 8, { align: "center", baseline: "top" });
    }
  }

  return pdf;
}

export async function downloadGenieDocumentPdf(
  doc: GeneratedGenieDocument,
  brand: GenieDocumentBrand,
): Promise<void> {
  const [customerLogo, hgWordmark] = await Promise.all([
    loadImage(brand.logoUrl ?? null, true),
    loadImage("/helpgenie.png", false),
  ]);
  const pdf = await buildGenieDocumentPdf(doc, brand, { customerLogo, hgWordmark });

  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${sanitize(brand.name)}-${sanitize(doc.title)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
